import React, { FC, useState } from 'react';
import { Box, Grid, Typography, TextField, Button, IconButton, useMediaQuery } from '@mui/material';
import { Send, AttachEmail } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { IBuildMessage } from '../../../../models';
import { MessageHistory } from './message-history';
import { MessagePreview } from './message-preview';
import { postBuildCommunicationMessage } from '../../../../fetch';
import { allImageFileTypesWithVideo } from '../../../../components/file/utils';

interface IHomeownerMessagingProps {
  buildId: number;
}

export const HomeownerMessaging: FC<IHomeownerMessagingProps> = ({ buildId }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 960px)');

  const [selectedMessage, setSelectedMessage] = useState<IBuildMessage | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(true);

  const fileTypes = allImageFileTypesWithVideo.filter(x => x !== '.tiff');

  const handleFileChange = (file: File) => {
    if (file) {
      const uploadedFileType = file.type.split?.('/')?.[1];
      if (
        //The mime type for .mov files is 'video/quicktime' they don't use the extension like other file types
        uploadedFileType !== 'quicktime' &&
        !fileTypes
          .map(extension => extension.replace(/\./g, ''))
          .some(type => type === uploadedFileType)
      ) {
        enqueueSnackbar(
          `Invalid file type, ${uploadedFileType}. Please upload one of these image types, ${fileTypes.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleCapture = async (target: EventTarget & HTMLInputElement) => {
    if (target.files && target.files.length > 0) {
      handleFileChange(target.files[0]);
    }
  };

  const sendMessageMutation = useMutation(
    async (data: { file?: File; message: string }) => {
      const formData = new FormData();
      if (data.file) {
        formData.append('UploadedFile', data.file);
      }
      formData.append('Message', data.message);

      return await postBuildCommunicationMessage(buildId, formData);
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Message sent successfully', { variant: 'success' });
        handleClearForm(); // Reset form
        // Invalidate the messages query cache to trigger a refresh
        queryClient.invalidateQueries(['getBuildCommunicationMessages', buildId]);
      },
      onError: (error: any) => {
        enqueueSnackbar('Error sending message. Please try again.', { variant: 'error' });
      }
    }
  );

  const handleSendMessage = async () => {
    if (!message.trim()) {
      enqueueSnackbar('Please enter a message', { variant: 'warning' });
      return;
    }

    sendMessageMutation.mutate({
      file: selectedFile || undefined,
      message: message.trim()
    });
  };

  const handleClearForm = () => {
    setSelectedFile(null);
    setMessage('');
  };

  const handleMessageSelect = (message: IBuildMessage) => {
    setSelectedMessage(message);
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
  };

  const isSendDisabled = !message.trim() || sendMessageMutation.isLoading;

  return (
    <Box className={classes.container}>
      <Grid container spacing={isMobile ? 1 : 3}>
        {/* History Section */}
        <Grid item xs={12} md={6}>
          <MessageHistory
            buildId={buildId}
            onMessageSelect={handleMessageSelect}
            selectedMessage={selectedMessage}
            onLoadingChange={setIsHistoryLoading}
          />
        </Grid>

        {/* Message Preview and Reply Section */}
        <Grid item xs={12} md={6}>
          <Box className={classes.messagingSection}>
            <Box mb={3}>
              <MessagePreview message={selectedMessage} />
            </Box>

            {!isHistoryLoading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Reply
                </Typography>

                {/* Image Preview and Reply Input Row */}
                <Box display="flex" gap={2} mb={1.5}>
                  {/* Message Input */}
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Type here"
                      placeholder="Type your message..."
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      disabled={sendMessageMutation.isLoading}
                      required
                    />
                  </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  {/* File Upload */}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={isMobile ? 0.5 : 1}
                    flexWrap="wrap"
                    flex={1}
                  >
                    <input
                      accept={fileTypes.join(',')}
                      disabled={sendMessageMutation.isLoading}
                      className={classes.hiddenInput}
                      id="message-attachment-input"
                      type="file"
                      onChange={e => {
                        handleCapture(e.target);
                        // reset the input value so you can re-upload the same photo if you want to
                        e.target.value = '';
                      }}
                    />
                    <label htmlFor="message-attachment-input">
                      <IconButton
                        color="primary"
                        disabled={sendMessageMutation.isLoading}
                        aria-label="attach image"
                        component="span"
                        size={isMobile ? 'small' : 'medium'}
                      >
                        <AttachEmail fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </label>
                    {selectedFile && (
                      <>
                        <Typography
                          variant={isMobile ? 'caption' : 'body2'}
                          color="textSecondary"
                          sx={{
                            maxWidth: isMobile ? '100px' : '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {selectedFile.name}
                        </Typography>
                        <IconButton
                          size={isMobile ? 'small' : 'medium'}
                          onClick={handleRemoveImage}
                          disabled={sendMessageMutation.isLoading}
                          sx={{ padding: isMobile ? '2px' : '8px' }}
                        >
                          ×
                        </IconButton>
                      </>
                    )}
                  </Box>

                  {/* Send Button */}
                  <Button
                    disabled={isSendDisabled}
                    type="button"
                    onClick={handleSendMessage}
                    startIcon={<Send />}
                    variant="contained"
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                  >
                    SEND
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(1),
    minHeight: '600px',
    [theme.breakpoints.down('md')]: {
      padding: 0
    }
  },
  messagingSection: {
    backgroundColor: '#fff',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `1px solid ${theme.palette.grey[300]}`,
    [theme.breakpoints.down('md')]: {
      borderLeft: 'none'
    }
  },
  hiddenInput: {
    display: 'none'
  }
}));
